import { z } from 'zod'
import { ProposalStatus, Currency } from '../types/enums'

/** Teklif PDF tasarımları — yeni tasarım: backend proposal-pdf.service DESIGNS + frontend ProposalDesignPicker + i18n `proposalTemplates:designs` */
export const PROPOSAL_DESIGNS = ['corporate', 'minimal', 'navy', 'construction', 'modern'] as const
export type ProposalDesign = (typeof PROPOSAL_DESIGNS)[number]

const ProposalItemSchema = z.object({
  description: z.string({ required_error: 'Kalem açıklaması zorunludur.' }).min(3).max(300),
  estimatedHours: z
    .number({ required_error: 'Tahmini saat zorunludur.' })
    .min(0.5, 'Tahmini saat en az 0.5 olmalıdır.'),
  /** Birim fiyat — string, backend'de Decimal */
  unitPrice: z
    .string({ required_error: 'Birim fiyat zorunludur.' })
    .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir fiyat giriniz.'),
  sortOrder: z.number().int().min(0).default(0),
})

/** Teklifteki standart metin bölümü (alt başlık + metin; `## başlık`, `- madde`, `**kalın**` biçimi desteklenir). id yoksa sunucu üretir. */
export const ProposalSectionSchema = z.object({
  id: z.string().max(64).optional(),
  title: z.string().trim().min(1, 'Bölüm başlığı zorunludur.').max(200),
  content: z.string().max(4000).default(''),
})

export const CreateProposalSchema = z.object({
  customerId: z.string({ required_error: 'Müşteri seçimi zorunludur.' }).uuid(),
  title: z
    .string({ required_error: 'Teklif başlığı zorunludur.' })
    .min(5, 'Başlık en az 5 karakter olmalıdır.')
    .max(200),
  description: z.string().max(2000).nullable().optional(),
  /** Şartlar: ödeme koşulları, süre, geçerlilik vb. (serbest metin) */
  terms: z.string().max(4000).nullable().optional(),
  design: z.enum(PROPOSAL_DESIGNS).default('corporate'),
  /** Verilmezse firmanın "otomatik ekle" standart metinleri eklenir; verilirse (boş dizi dahil) aynen kullanılır */
  sections: z.array(ProposalSectionSchema).max(50).optional(),
  estimatedStartDate: z.string().date().nullable().optional(),
  estimatedEndDate: z.string().date().nullable().optional(),
  currency: z.nativeEnum(Currency).default(Currency.TRY),
  /** En az 1 kalem zorunludur */
  items: z
    .array(ProposalItemSchema)
    .min(1, 'Teklifte en az 1 iş kalemi olmalıdır.'),
})

export const UpdateProposalSchema = z.object({
  title: z.string().min(5).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  terms: z.string().max(4000).nullable().optional(),
  design: z.enum(PROPOSAL_DESIGNS).optional(),
  /** Verilirse bölümlerin tamamı bu liste ile değişir */
  sections: z.array(ProposalSectionSchema).max(50).optional(),
  estimatedStartDate: z.string().date().nullable().optional(),
  estimatedEndDate: z.string().date().nullable().optional(),
  currency: z.nativeEnum(Currency).optional(),
  items: z.array(ProposalItemSchema).min(1).optional(),
})

export const UpdateProposalStatusSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal(ProposalStatus.DRAFT),
  }),
  z.object({
    status: z.literal(ProposalStatus.PENDING_APPROVAL),
  }),
  z.object({
    status: z.literal(ProposalStatus.SENT),
  }),
  z.object({
    status: z.literal(ProposalStatus.WON),
    assignedPmId: z.string({ required_error: 'PM ataması zorunludur.' }).uuid(),
  }),
  z.object({
    status: z.literal(ProposalStatus.LOST),
    /** [IK bölüm 5.4] Red nedeni zorunlu */
    lossReason: z
      .string({ required_error: 'Kayıp nedeni zorunludur.' })
      .min(10, 'Kayıp nedeni en az 10 karakter olmalıdır.'),
  }),
  z.object({
    status: z.literal(ProposalStatus.CANCELLED),
  }),
])

/** Teklif şablonu kalemi — taslak olduğu için boş açıklama/fiyat serbest */
const ProposalTemplateItemSchema = z.object({
  description: z.string().max(300),
  estimatedHours: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir sayı giriniz.'),
  unitPrice: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir fiyat giriniz.'),
})

export const CreateProposalTemplateSchema = z.object({
  name: z.string({ required_error: 'Şablon adı zorunludur.' }).trim().min(2, 'Şablon adı en az 2 karakter olmalıdır.').max(120),
  title: z.string().max(200).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  terms: z.string().max(4000).nullable().optional(),
  currency: z.nativeEnum(Currency).default(Currency.TRY),
  items: z.array(ProposalTemplateItemSchema).max(100).default([]),
})

export const UpdateProposalTemplateSchema = CreateProposalTemplateSchema.partial()

/** Firma standart metin kütüphanesi (Teklifler > Standart Metinler) */
export const CreateProposalStandardTextSchema = z.object({
  title: z.string({ required_error: 'Başlık zorunludur.' }).trim().min(1, 'Başlık zorunludur.').max(200),
  content: z.string().max(4000).default(''),
  /** Yeni tekliflere otomatik eklensin mi */
  autoInclude: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
})
export const UpdateProposalStandardTextSchema = CreateProposalStandardTextSchema.partial()

export type ProposalSection = z.infer<typeof ProposalSectionSchema>
export type CreateProposalStandardTextInput = z.infer<typeof CreateProposalStandardTextSchema>
export type UpdateProposalStandardTextInput = z.infer<typeof UpdateProposalStandardTextSchema>
export type CreateProposalTemplateInput = z.infer<typeof CreateProposalTemplateSchema>
export type UpdateProposalTemplateInput = z.infer<typeof UpdateProposalTemplateSchema>
export type CreateProposalInput = z.infer<typeof CreateProposalSchema>
export type UpdateProposalInput = z.infer<typeof UpdateProposalSchema>
export type UpdateProposalStatusInput = z.infer<typeof UpdateProposalStatusSchema>
